import React, { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";

let _sdkPromise = null;
const loadPayPalSDK = (clientId) => {
    if (_sdkPromise) return _sdkPromise;
    _sdkPromise = new Promise((resolve, reject) => {
        if (window.paypal) return resolve(window.paypal);
        const s = document.createElement("script");
        const params = new URLSearchParams({
            "client-id": clientId,
            currency: "USD",
            intent: "capture",
            "enable-funding": "venmo,paylater",
            components: "buttons",
        });
        s.src = `https://www.paypal.com/sdk/js?${params.toString()}`;
        s.async = true;
        s.onload = () => resolve(window.paypal);
        s.onerror = () => {
            _sdkPromise = null;
            reject(new Error("Failed to load PayPal SDK"));
        };
        document.head.appendChild(s);
    });
    return _sdkPromise;
};

/**
 * Reusable PayPal Smart Buttons (incl. Venmo).
 * Props:
 *  - createOrder: async () => orderId   (calls backend to create an order)
 *  - captureType: "donation" | "ticket" | "auction_winner"
 *  - onSuccess: (captureResult) => void
 *  - onError: (err) => void
 *  - disabled: boolean
 *  - validate: () => string | null  (return error message to block click)
 */
export default function PayPalButtons({
    createOrder,
    captureType,
    captureOrder,
    onSuccess,
    onError,
    disabled = false,
    validate,
}) {
    const containerRef = useRef(null);
    const buttonsRef = useRef(null);
    const [ready, setReady] = useState(false);
    const [loadErr, setLoadErr] = useState("");
    const [clientId, setClientId] = useState("");

    // Keep latest callbacks in refs to avoid re-rendering buttons on prop change
    const cbRef = useRef({ createOrder, validate, captureType, captureOrder, onSuccess, onError });
    useEffect(() => {
        cbRef.current = { createOrder, validate, captureType, captureOrder, onSuccess, onError };
    }, [createOrder, validate, captureType, captureOrder, onSuccess, onError]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get("/paypal/config");
                if (cancelled) return;
                if (!data.client_id) {
                    setLoadErr("PayPal not configured");
                    return;
                }
                setClientId(data.client_id);
                await loadPayPalSDK(data.client_id);
                if (cancelled) return;
                setReady(true);
            } catch (e) {
                setLoadErr("Could not load PayPal");
            }
        })();
        return () => { cancelled = true; };
    }, []);

    useEffect(() => {
        if (!ready || !window.paypal || !containerRef.current) return;
        // Clean up any existing button
        if (buttonsRef.current) {
            try { buttonsRef.current.close(); } catch (_) {}
            buttonsRef.current = null;
        }
        containerRef.current.innerHTML = "";
        const btns = window.paypal.Buttons({
            style: { layout: "vertical", shape: "rect", color: "gold", label: "paypal", height: 45 },
            onClick: (data, actions) => {
                const v = cbRef.current.validate?.();
                if (v) {
                    cbRef.current.onError?.(new Error(v));
                    return actions.reject();
                }
                return actions.resolve();
            },
            createOrder: async () => {
                try {
                    const id = await cbRef.current.createOrder();
                    return id;
                } catch (e) {
                    cbRef.current.onError?.(e);
                    throw e;
                }
            },
            onApprove: async (data) => {
                try {
                    if (cbRef.current.captureOrder) {
                        const res = await cbRef.current.captureOrder(data.orderID);
                        cbRef.current.onSuccess?.(res);
                        return;
                    }
                    const res = await api.post("/paypal/capture-order", {
                        order_id: data.orderID,
                        type: cbRef.current.captureType,
                    });
                    cbRef.current.onSuccess?.(res.data);
                } catch (e) {
                    cbRef.current.onError?.(e);
                }
            },
            onError: (err) => {
                cbRef.current.onError?.(err);
            },
        });
        buttonsRef.current = btns;
        btns.render(containerRef.current).catch((e) => setLoadErr(e?.message || "PayPal render error"));
        return () => {
            try { btns.close(); } catch (_) {}
        };
    }, [ready, clientId]);

    if (loadErr) {
        return <p className="text-xs text-muted-foreground" data-testid="paypal-error">{loadErr}</p>;
    }
    return (
        <div className={disabled ? "opacity-50 pointer-events-none" : ""} data-testid="paypal-buttons-wrapper">
            {!ready && <p className="text-xs text-muted-foreground">Loading PayPal…</p>}
            <div ref={containerRef} data-testid="paypal-buttons-container" />
        </div>
    );
}
